import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { CABECALHO_CSRF } from '../../auth';
import { hojeBrasilia, menosDias } from '../../central';
import { buscar } from '../../buscar';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { CorDaFaixa, IndicadorDaFaixa } from '../../vendas';

/**
 * ALERTA DE MARGEM E DESCONTO (Carlos, 28/09): todos os balconistas do período com a COR da
 * margem e do desconto médio (sem genérico e similar — dono, 28/09) — quem está no vermelho
 * aparece primeiro. Dado da LOJA (só o dono). Embaixo, as faixas, editáveis com motivo e versão;
 * o balconista vê as mesmas cores, só com os números dele, no Meu desempenho.
 */

interface Faixas {
  readonly margem: { amarelo: number; azul: number; verde: number };
  readonly desconto: { amarelo: number; vermelho: number };
  readonly descontoGenerico: { amarelo: number; vermelho: number };
  readonly gruposGenerico: readonly string[];
}
interface Linha {
  readonly chave: string;
  readonly nome: string;
  readonly vendido: string;
  readonly margem: IndicadorDaFaixa;
  readonly desconto: IndicadorDaFaixa;
  readonly descontoGenerico: IndicadorDaFaixa;
  readonly emAlerta: boolean;
}

type Periodo = 'mes' | '7' | 'hoje';
const PERIODOS: readonly [Periodo, string][] = [
  ['mes', 'Mês'],
  ['7', '7 dias'],
  ['hoje', 'Hoje'],
];
function intervalo(p: Periodo) {
  const h = hojeBrasilia();
  if (p === 'hoje') return { de: h, ate: h };
  if (p === '7') return { de: menosDias(h, 6), ate: h };
  return { de: `${h.slice(0, 8)}01`, ate: h };
}

async function lerJson<T>(caminho: string): Promise<T | null> {
  try {
    const r = await buscar(caminho, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    return r.ok ? ((await r.json()) as T) : null;
  } catch {
    return null;
  }
}

const pct = (v: number | null) =>
  v === null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const brl = (v: string) =>
  Number(v).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
const ROTULO: Record<CorDaFaixa, string> = {
  vermelho: 'vermelho',
  amarelo: 'amarelo',
  azul: 'azul',
  verde: 'verde',
};

export function AlertasDeMargem({ sessao }: { sessao: Sessao }) {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [soAlerta, setSoAlerta] = useState(false);
  const [dados, setDados] = useState<{ faixas: Faixas; balconistas: Linha[] } | null>(null);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(() => {
    setErro(false);
    setDados(null);
    const { de, ate } = intervalo(periodo);
    void lerJson<{ faixas: Faixas; balconistas: Linha[] }>(
      `/api/central/alertas-margem?de=${de}&ate=${ate}`,
    ).then((d) => {
      if (d) setDados(d);
      else setErro(true);
    });
  }, [periodo]);
  useEffect(carregar, [carregar]);

  const linhas = (dados?.balconistas ?? []).filter((l) => !soAlerta || l.emAlerta);
  const emAlerta = dados?.balconistas.filter((l) => l.emAlerta).length ?? 0;

  return (
    <div className="cv-corpo">
      <div className="dc-alertas-topo">
        <div className="cv-segmento" role="tablist" aria-label="Período">
          {PERIODOS.map(([p, r]) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={periodo === p}
              className={periodo === p ? 'cv-sel' : ''}
              onClick={() => setPeriodo(p)}
            >
              {r}
            </button>
          ))}
        </div>
        <label className="dc-alertas-so">
          <input
            type="checkbox"
            checked={soAlerta}
            onChange={(e) => setSoAlerta(e.target.checked)}
          />
          Só quem está no vermelho
        </label>
        {dados && (
          <span className={emAlerta ? 'dc-alertas-conta dc-cor-vermelho' : 'dc-alertas-conta'}>
            {emAlerta
              ? `${String(emAlerta)} de ${String(dados.balconistas.length)} no vermelho`
              : 'ninguém no vermelho'}
          </span>
        )}
      </div>

      {erro ? (
        <ErroDeArea oQue="o alerta de margem" aoTentar={carregar} />
      ) : !dados ? (
        <div className="cv-carregando" aria-busy="true" />
      ) : (
        <div className="cv-cartao dc-alertas-tabela">
          <table className="cv-tabela">
            <thead>
              <tr>
                <th>Balconista</th>
                <th className="cv-dir">Vendido</th>
                <th className="cv-dir">Margem</th>
                <th className="cv-dir">Desconto médio (sem genérico e similar)</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.chave}>
                  <td>
                    {l.emAlerta && <Icone nome="warn" tamanho={13} traco={1.8} />} {l.nome}{' '}
                    <span className="cv-cinza">cód {l.chave}</span>
                  </td>
                  <td className="cv-dir">{brl(l.vendido)}</td>
                  <Celula i={l.margem} />
                  <Celula i={l.desconto} />
                </tr>
              ))}
              {!linhas.length && (
                <tr>
                  <td colSpan={4} className="cv-cinza">
                    Ninguém {soAlerta ? 'no vermelho' : 'com venda'} no período.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="cv-cinza cv-peq">
            Margem só conta itens com custo do HOS (desde 25/09). Só aparecem vendedores com código
            do HOS. Clique em Balconistas para ver os produtos e os cupons de cada um.
          </p>
        </div>
      )}

      {dados && <EditarFaixas sessao={sessao} atual={dados.faixas} aoSalvar={carregar} />}
    </div>
  );
}

function Celula({ i }: { i: IndicadorDaFaixa }) {
  return (
    <td
      className={`cv-dir dc-cel${i.cor ? ` dc-cor-${i.cor}` : ''}`}
      title={i.cor ? `faixa ${ROTULO[i.cor]}` : 'sem dado'}
    >
      {pct(i.valor)}
    </td>
  );
}

function EditarFaixas({
  sessao,
  atual,
  aoSalvar,
}: {
  sessao: Sessao;
  atual: Faixas;
  aoSalvar: () => void;
}) {
  const [f, setF] = useState<Faixas>(atual);
  const [grupos, setGrupos] = useState(atual.gruposGenerico.join(', '));
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);
  useEffect(() => {
    setF(atual);
    setGrupos(atual.gruposGenerico.join(', '));
  }, [atual]);

  const num = (v: number, set: (n: number) => void, rotulo: string) => (
    <label className="gc-campo">
      <span>{rotulo}</span>
      <span className="gc-num">
        <input
          type="number"
          inputMode="decimal"
          step="0.5"
          value={Number.isFinite(v) ? v : ''}
          onChange={(e) => set(Number(e.target.value))}
        />
        <em>%</em>
      </span>
    </label>
  );

  const salvar = async () => {
    if (salvando) return;
    setSalvando(true);
    setAviso(null);
    const faixas = {
      ...f,
      gruposGenerico: grupos
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean),
    };
    let texto = '';
    let ok = false;
    try {
      const r = await fetch('/api/central/faixas-margem', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
        body: JSON.stringify({ faixas, motivo }),
      });
      ok = r.ok;
      if (r.ok)
        texto = `Faixas salvas (versão ${String(((await r.json()) as { versao: number }).versao)}).`;
      else
        texto =
          r.status >= 500
            ? 'Não deu para confirmar se salvou. Recarregue e confira.'
            : (((await r.json().catch(() => ({}))) as { error?: { message?: string } }).error
                ?.message ?? 'Não foi aceito.');
    } catch {
      texto = 'Sem conexão. Confira antes de repetir.';
    }
    setSalvando(false);
    setAviso({ tom: ok ? 'ok' : 'erro', texto });
    if (ok) {
      setMotivo('');
      aoSalvar();
    }
  };

  return (
    <div className="cv-cartao gc-cartao dc-faixas">
      <h2>Faixas de margem e desconto</h2>
      <p className="gc-nota">
        Valem para este alerta e para o que cada balconista vê no Meu desempenho (só os números
        dele). Margem: abaixo do amarelo é vermelho. Desconto: até o amarelo é verde; acima do
        vermelho, vermelho.
      </p>
      <div className="gc-linha">
        {num(
          f.margem.amarelo,
          (n) => setF({ ...f, margem: { ...f.margem, amarelo: n } }),
          'Margem: amarelo a partir de',
        )}
        {num(
          f.margem.azul,
          (n) => setF({ ...f, margem: { ...f.margem, azul: n } }),
          'azul a partir de',
        )}
        {num(
          f.margem.verde,
          (n) => setF({ ...f, margem: { ...f.margem, verde: n } }),
          'verde a partir de',
        )}
      </div>
      <div className="gc-linha">
        {num(
          f.desconto.amarelo,
          (n) => setF({ ...f, desconto: { ...f.desconto, amarelo: n } }),
          'Desconto: verde até',
        )}
        {num(
          f.desconto.vermelho,
          (n) => setF({ ...f, desconto: { ...f.desconto, vermelho: n } }),
          'amarelo até',
        )}
      </div>
      <label className="gc-campo">
        <span>Grupos do HOS fora do desconto médio (separados por vírgula)</span>
        <input value={grupos} onChange={(e) => setGrupos(e.target.value)} />
      </label>
      <label className="gc-campo">
        <span>
          Motivo da mudança <b>obrigatório</b>
        </span>
        <textarea
          rows={2}
          maxLength={500}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      <div className="gc-acoes">
        <button
          type="button"
          className="cv-botao cv-botao-primario"
          disabled={motivo.trim().length < 5 || salvando}
          onClick={() => void salvar()}
        >
          <Icone nome="check" tamanho={14} traco={1.8} />
          {salvando ? 'Salvando…' : 'Salvar faixas'}
        </button>
      </div>
      {aviso && (
        <p className={aviso.tom === 'ok' ? 'gc-ok' : 'gc-erro'} role="status">
          {aviso.texto}
        </p>
      )}
    </div>
  );
}
